/**
 * MaterialQtyTable — operator allocates a fixed total quantity across
 * raw materials linked to a supplier. Mirror of VariantQtyTable; first
 * column shows the material name + unit instead of the variant axes
 * label.
 *
 * Pure presentational — owns no state, no fetch. The parent owns the
 * per-material qty map and the validation / submit flow.
 */

import { Input } from '../atoms/Input'
import { Th, Td } from './Table'
import { TableShell } from './TableShell'

export interface MaterialQtyRow {
  readonly raw_material_id: string
  readonly raw_material_name: string
  readonly unit_label: string | null
  readonly on_hand: number
}

interface Props {
  readonly materials: ReadonlyArray<MaterialQtyRow>
  /** Per-material qty entered so far (whole units, integer ≥ 0). */
  readonly qtyByMaterial: Readonly<Record<string, number>>
  readonly onQtyChange: (rawMaterialId: string, qty: number) => void
  /** Same fungible-units capacity as the modal's invoice remaining. */
  readonly remaining?: number
  readonly disabled?: boolean
}

function parseQtyInput(raw: string): number {
  const t = raw.trim()
  if (t === '') return 0
  const n = Number(t)
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return NaN
  return n
}

export function MaterialQtyTable({
  materials,
  qtyByMaterial,
  onQtyChange,
  remaining,
  disabled = false
}: Props): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Material</Th>
            <Th className="text-right">On hand</Th>
            <Th className="text-right">Receive qty</Th>
            {remaining !== undefined ? <Th className="text-right">Remaining</Th> : null}
          </tr>
        </thead>
        <tbody>
          {materials.map((m) => {
            const current = qtyByMaterial[m.raw_material_id] ?? 0
            return (
              <tr key={m.raw_material_id} className="border-t border-border">
                <Td>
                  <div className="font-medium text-charcoal">{m.raw_material_name}</div>
                  {m.unit_label !== null ? (
                    <div className="text-text-secondary text-body-xs">{m.unit_label}</div>
                  ) : null}
                </Td>
                <Td className="text-right whitespace-nowrap font-mono">{m.on_hand}</Td>
                <Td className="text-right whitespace-nowrap">
                  <Input
                    value={current === 0 ? '' : String(current)}
                    onChange={(e) => {
                      const parsed = parseQtyInput(e.target.value)
                      onQtyChange(m.raw_material_id, Number.isNaN(parsed) ? 0 : parsed)
                    }}
                    disabled={disabled}
                    inputMode="numeric"
                    placeholder="0"
                    aria-label={`Receive qty for ${m.raw_material_name}`}
                    className="w-24 text-right"
                  />
                </Td>
                {remaining !== undefined ? (
                  <Td className="text-right whitespace-nowrap font-mono">{remaining}</Td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
